import React, { Dispatch, SetStateAction } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import './add-project-popup.scss';
import { projectsApi } from '@api/projectsApi';
import Modal from '@components/Modal/Modal';
import { isErrorResponse } from '@utils/utils';
import { useToastContext } from '@context/toastContext';
import { useTheme } from '@context/themeContext';
import { useProjectContext } from '@context/ProjectContext';
import Button from '@components/buttons/Button';
import { addedProjectId } from '@redux/reducers/projectsReducer';
import { LoadingBackdrop } from '@components/LoadingBackdrop/LoadingBackdrop';

export type AddProjectPopupProps = {
    setVisible: Dispatch<SetStateAction<any>>;
    onProjectCreation: (projectId: string) => void;
};

type PROJECT_CREATION_OPTIONS = 'generate' | 'load';

const AddProjectPopup: React.FC<AddProjectPopupProps> = ({ setVisible, onProjectCreation }) => {
    const [loading, setLoading] = React.useState<boolean>(false);
    const dispatch = useDispatch();
    const { loadProjects } = useProjectContext();
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { addToast } = useToastContext();
    const { theme } = useTheme();

    const onSubmit = async event => {
        event.preventDefault();

        setLoading(true);

        const formData = new FormData(event.target);
        const formDataObj = {} as {
            name: string;
            code: number;
            projectCreationType: PROJECT_CREATION_OPTIONS;
            copiedProjectId?: string;
        };
        for (const [key, val] of formData) {
            if (val) {
                formDataObj[key] = val;
            }
        }

        const { projectCreationType } = formDataObj;

        const data = await projectsApi.createProject({
            name: formDataObj.name,
            code: formDataObj.code,
        });

        if (isErrorResponse(data)) {
            addToast(t('toasts.error.project-name-already-exists'), 'error');
        }

        if (data.projectId) {
            const { projectId } = data;

            dispatch(addedProjectId(projectId));
            if (projectCreationType === 'load') {
                setVisible('creation');
            }
            if (projectCreationType === 'generate') {
                await loadProjects();

                setLoading(false);
                setVisible('');
                onProjectCreation(data.projectId);
            }
        }

        setLoading(false);
    };

    return (
        <LoadingBackdrop isVisible={loading} transparent>
            <Modal title={t('add-project-popup.model-title')} setVisible={setVisible}>
                <form onSubmit={onSubmit}>
                    <div className={`addmodel-input-block ${theme}`}>
                        <label htmlFor="projName">{t('add-project-popup.project-name')}</label>
                        <input name="name" required id="projName" type="text" autoFocus />
                    </div>
                    <div className="addmodel-input-block">
                        <input name="code" id="code" type="number" defaultValue={25832} hidden />
                    </div>
                    <div className="nav-block">
                        <span className="nav-block-header">
                            {t('add-project-popup.model-generation-method')}
                        </span>
                        <div className="input-block">
                            <input
                                id="generate"
                                name="projectCreationType"
                                type="radio"
                                defaultChecked
                                defaultValue="generate"
                            />
                            <label htmlFor="generate">
                                <span className="label-header">
                                    {t('add-project-popup.generate')}
                                </span>
                                <span className="label-text">
                                    {t('add-project-popup.automatic-generation')}
                                </span>
                            </label>
                        </div>
                        <div className="input-block">
                            <input
                                id="load"
                                name="projectCreationType"
                                type="radio"
                                defaultValue="load"
                            />
                            <label htmlFor="load">
                                <span className="label-header">
                                    {t('add-project-popup.download-initial-files')}
                                </span>
                                <span className="label-text">
                                    {t('add-project-popup.create-by-user-files')}
                                </span>
                            </label>
                        </div>
                    </div>
                    <div className="button-block-addmodel">
                        <div className="button-block">
                            <Button
                                onClick={() => setVisible('')}
                                text={t('add-project-popup.cancel')}
                                colorType="transparent"
                            />
                            <Button
                                type="submit"
                                text={t('add-project-popup.continue')}
                                colorType="dark"
                                disabled={loading}
                            />
                        </div>
                    </div>
                </form>
            </Modal>
        </LoadingBackdrop>
    );
};

export default AddProjectPopup;
